/**
 * Accessibility enhancements (WCAG 3.2.5 — links opening a new window).
 * Appends a visually-hidden "(opens in new window)" to every target="_blank"
 * link that doesn't already announce it. Runs once on load and after DOM
 * mutations so app-injected links (slide cart, Klaviyo, etc.) are covered too.
 */
(function () {
  var NOTICE = '(opens in new window)';

  function annotate(root) {
    var links = (root || document).querySelectorAll('a[target="_blank"]:not([data-newwin-annotated])');
    links.forEach(function (a) {
      a.setAttribute('data-newwin-annotated', 'true');

      // Skip if the link already conveys it via aria-label or existing text.
      var aria = (a.getAttribute('aria-label') || '').toLowerCase();
      if (aria.indexOf('new window') !== -1 || aria.indexOf('new tab') !== -1) return;
      if ((a.textContent || '').toLowerCase().indexOf('new window') !== -1) return;

      var span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = ' ' + NOTICE;
      a.appendChild(span);

      // Security hardening for _blank links that lack it.
      var rel = a.getAttribute('rel') || '';
      if (rel.indexOf('noopener') === -1) {
        a.setAttribute('rel', (rel + ' noopener').trim());
      }
    });
  }

  /**
   * WCAG 2.1.1 — click-only accordion toggles rendered as plain headings.
   * Merchant-content FAQs (.faqpage .accordion) and the collection filter
   * group titles are <h3>s with click handlers: unfocusable and keyboard-dead.
   * Wrap each heading's content in a real <button> (keeps heading semantics,
   * native Enter/Space fires click, which bubbles to the existing handlers)
   * and keep aria-expanded in sync with the open/active classes.
   */
  var ACCORDION_TOGGLES = '.faqpage .accordion, .productgrid--sidebar-title--small';

  function upgradeClickAccordions(root) {
    (root || document).querySelectorAll(ACCORDION_TOGGLES).forEach(function (h) {
      if (h.hasAttribute('data-a11y-upgraded')) return;
      h.setAttribute('data-a11y-upgraded', 'true');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-acc-btn';
      while (h.firstChild) btn.appendChild(h.firstChild);
      h.appendChild(btn);

      function syncExpanded() {
        var expanded = h.classList.contains('active') || h.classList.contains('open');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
      syncExpanded();
      h.addEventListener('click', function () { setTimeout(syncExpanded, 0); });
    });
  }

  /**
   * Third-party widget patches (audit: aria-command-name, tabs-missing-aria-selected).
   * - SlideCart HQ's click-to-close overlay is a role="button" div with no
   *   accessible name — give it one when it appears.
   * - Okendo's review tabs use roving tabindex but omit aria-selected — derive
   *   it from tabindex (the active tab is the one with tabindex 0).
   */
  function patchThirdParty(root) {
    (root || document).querySelectorAll('.slidecarthq-overlay:not([aria-label])').forEach(function (el) {
      el.setAttribute('aria-label', 'Close cart');
    });

    // xCloud Search & Filter mobile overlay: its Cancel control is an empty
    // <a href="" role="button"> — no accessible name for screen readers.
    (root || document).querySelectorAll('.cloud-search-mobile__cancel:not([data-a11y-named])').forEach(function (a) {
      a.setAttribute('data-a11y-named', 'true');
      if (!a.getAttribute('aria-label') && !a.textContent.trim()) {
        a.setAttribute('aria-label', 'Cancel search');
      }
    });

    // Review-stars divs (e.g. .frs-stars) carry aria-label without a role that
    // permits naming (audit: aria-prohibited-attr) — role="img" makes it valid.
    (root || document).querySelectorAll('.frs-stars[aria-label]:not([role])').forEach(function (el) {
      el.setAttribute('role', 'img');
    });

    (root || document).querySelectorAll('[role="tab"]').forEach(function (tab) {
      var selected = tab.getAttribute('tabindex') !== '-1';
      var current = tab.getAttribute('aria-selected');
      var expected = selected ? 'true' : 'false';
      if (current !== expected) tab.setAttribute('aria-selected', expected);
    });
  }

  /**
   * WCAG 2.1.2 No Keyboard Trap — the xCloud Search & Filter app's mobile
   * search overlay (role="dialog", aria-modal) traps Tab between its input
   * and Cancel link with no Escape handling. Esc now activates the app's own
   * Cancel control; if the app's click handler is missing, the anchor's
   * href="" navigation still dismisses the overlay.
   */
  function bindSearchOverlayEscape() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      var cancel = document.querySelector('.cloud-search-mobile__cancel');
      if (cancel && cancel.offsetParent !== null) {
        e.preventDefault();
        cancel.click();
      }
    }, true);
  }

  /**
   * WCAG 1.3.1 — the Slide Cart THQ app renders <header class="header"> inside
   * its cart drawer, which exposes a SECOND "banner" landmark (axe
   * landmark-no-duplicate-banner / landmark-unique). The theme header is the
   * store's real banner; strip the landmark role off the app's drawer header
   * (its heading text is preserved).
   */
  function dedupeBanners(root) {
    (root || document)
      .querySelectorAll('[class*="slidecart"] header:not([data-banner-fixed])')
      .forEach(function (h) {
        h.setAttribute('data-banner-fixed', 'true');
        h.setAttribute('role', 'none');
      });
  }

  function init() {
    annotate(document);
    upgradeClickAccordions(document);
    patchThirdParty(document);
    dedupeBanners(document);
    bindSearchOverlayEscape();
    if (!('MutationObserver' in window)) return;
    var obs = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if ((m.addedNodes && m.addedNodes.length) || m.type === 'attributes') {
          annotate(document);
          upgradeClickAccordions(document);
          patchThirdParty(document);
          dedupeBanners(document);
          break;
        }
      }
    });
    // attributeFilter: tabindex — roving-tabindex tab widgets (Okendo) move
    // tabindex between tabs on selection; re-derive aria-selected when they do.
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
